import React from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import { alpha } from '@mui/material/styles'

import type { HolidayNavTab } from '@/src/types/holidays'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface HolidayNavTabsProps {
    tabs: HolidayNavTab[]
    activeId: string
    onTabClick: (id: string) => void
    isMobile: boolean
    /** Few enough tabs to spread edge to edge; otherwise they scroll. */
    spread: boolean
}

const HolidayNavTabs: React.FC<HolidayNavTabsProps> = ({ tabs, activeId, onTabClick, isMobile, spread }) => {
    return (
        /*
         * Stickiness belongs to the page. The bar itself is just a row of tabs over a hairline:
         * the card it used to sit in inset the first tab and drew a second edge under the one the
         * active tab already draws.
         */
        <Box sx={{ borderBottom: '1px solid', borderColor: 'borders.default' }}>
            <Tabs
                value={activeId}
                onChange={(_event, value: string) => onTabClick(value)}
                variant={spread ? 'fullWidth' : 'scrollable'}
                // No arrows: they ate two tab-widths on a phone, and the row swipes.
                scrollButtons={false}
                sx={{
                    minHeight: isMobile ? 48 : 60,
                    // The active tab is carried by a tinted pill and a rule beneath it, as in the
                    // new design — colour alone left the current section hard to spot in a row of
                    // seven. The rule is the tab's own border, so the indicator stays hidden.
                    '& .MuiTabs-indicator': { display: 'none' },
                }}
            >
                {tabs.map(tab => (
                    <Tab
                        key={tab.id}
                        value={tab.id}
                        label={tab.label}
                        sx={{
                            minHeight: isMobile ? 48 : 60,
                            px: isMobile ? 1.5 : 2,
                            fontSize: isMobile ? '1rem' : '0.938rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            color: 'textColors.subtitle',
                            borderBottom: '3px solid transparent',
                            mb: '-1px',
                            transition: 'background-color 0.2s, border-color 0.2s',
                            '&.Mui-selected': {
                                color: 'secondary.main',
                                fontWeight: 700,
                                bgcolor: theme => alpha(theme.palette.secondary.main, 0.08),
                                borderBottomColor: 'secondary.main',
                                borderTopLeftRadius: HOLIDAYS_RADIUS.NESTED * 8,
                                borderTopRightRadius: HOLIDAYS_RADIUS.NESTED * 8
                            }
                        }}
                    />
                ))}
            </Tabs>
        </Box>
    )
}

export { HolidayNavTabs }
export type { HolidayNavTabsProps }
export default HolidayNavTabs
